import $ from 'jquery'
import _ from 'underscore'
import messageResource from '../../messageResource'
import {CustomersProjectsSelectionQualifier} from '../../constants'
import {arrayToMap, arrayToSet} from '../../atUtils'
import UserSelectorUtils from '../../usersSelector/UserSelectorUtils'
import {customerNameLower, projectNameLower, userNameLower} from '../../util/pluralizeUtils'
import {getLoggedInUserId} from '../../loggedInUser/selectors'
import {unsafe_selectWithState} from '../../redux/utils'
import BaseReportParamsStringBuilder from './BaseReportParamsStringBuilder'
import {DateFormat} from '../../date_format'
import {DateRangeSelectionQualifier} from 'constants'
import {isTZGEnabled, isUGREnabled} from '../../features/selectors'
import BaseReportFormatUtils from '../FormatUtils'

export default class ATReportParamsStringBuilder extends BaseReportParamsStringBuilder {
    constructor({
        data,
        dateFormat,
        reportAuthorizationInfo,
        userFormatter = BaseReportFormatUtils.reportUserFormatterWithUsernameIfNeeded,
    }) {
        super({data, dateFormat, userFormatter})

        this.userGroupsFeatureEnabled = unsafe_selectWithState(state => isUGREnabled(state))
        this.tzGroupsFeatureEnabled = unsafe_selectWithState(state => isTZGEnabled(state))

        this.manageableUserIdsSet = !!data.manageableUserIds && arrayToSet(data.manageableUserIds)

        this.customersWithMaIds = !!data.customersWithMaIds && arrayToSet(data.customersWithMaIds)
        this.projectsWithMaIds = !!data.projectsWithMaIds && arrayToSet(data.projectsWithMaIds)

        this.loggedInUserId = unsafe_selectWithState(state => getLoggedInUserId(state))
        this.isViewerNotAssignedToTeam = data.manageableUserIds &&
                                         data.users &&
                                         data.manageableUserIds.indexOf(this.loggedInUserId) === -1 &&
                                         data.users.some(u => u.id === this.loggedInUserId)

        this.reportAuthorizationInfo = reportAuthorizationInfo

        this.customers = data.cpList ? arrayToMap(data.cpList.customers) : {}
        this.projects = data.cpList ? arrayToMap(data.cpList.projects) : {}
    }

    getFilteredCustomers(cpSelection) {
        return cpSelection.includeOnlyManagementScope ? [] : cpSelection.customers.map(c => this.customers[c.id]).filter(c => !!c)
    }

    getFilteredProjects(cpSelection) {
        const selectedProjectsIdsSet = arrayToSet(cpSelection.projects)
        let projectsFilterPredicate

        if (cpSelection.includeOnlyManagementScope) {
            const selectedCustomersIdsSet = arrayToSet(cpSelection.customers)
            projectsFilterPredicate = project => this.projectsWithMaIds[project.id]
                                                       && (selectedProjectsIdsSet[project.id] || selectedCustomersIdsSet[project.customerId])
        } else {
            projectsFilterPredicate = project => selectedProjectsIdsSet[project.id] && this.projects[project.id]
        }

        return Object.values(this.projects).filter(projectsFilterPredicate)
    }

    getUserSelectionParamString(userSelection) {
        if (!this.reportAuthorizationInfo.allUsersAvailable && !this.reportAuthorizationInfo.anyOtherUsersAvailable) {
            return this.userFormatter.format(this.getUser(this.loggedInUserId))
        }
        return super.getUserSelectionParamString(userSelection, () => this._getAllUsersMessage())
    }

    getCPSelectionParamString(cpSelection) {

        const AND_DELIMITER = " & "

        if (cpSelection.qualifier === CustomersProjectsSelectionQualifier.ALL)
            return messageResource.getMessage("saved_reports.cp_selector.all_projects")

        if (cpSelection.qualifier === CustomersProjectsSelectionQualifier.ALL_ACTIVE) {
            return messageResource.getMessage("saved_reports.cp_selector.all_active_projects")
        } else {
            let customers = this.getFilteredCustomers(cpSelection)
            let projects = this.getFilteredProjects(cpSelection)

            if (customers.length === 0 && projects.length === 0)
                return messageResource.getMessage("saved_reports.cp_selector.emty_selection") //error_class

            if (customers.length === 1 && projects.length === 0) {
                return messageResource.getMessage("saved_reports.cp_selector.single_element.customer",
                    [customers[0].name])
            }

            if (projects.length === 1 && customers.length === 0) {
                return  messageResource.getMessage("saved_reports.cp_selector.single_element.project",
                    [projects[0].name])
            }

            const getCustomersPart = (customers, availableSuffix, activeProjectsSuffix) =>
                customers.length.toString()
                + " "
                + customerNameLower(customers.length)
                + " "
                + messageResource.getMessage("saved_reports.cp_selector.multiple_customer", [
                    availableSuffix,
                    activeProjectsSuffix
                ])

            const getProjectsPart = (projects) => projects.length.toString() + " " + projectNameLower(projects.length)

            let customersPart = "", projectsPart = ""
            if (customers.length > 0) {
                customersPart = getCustomersPart(customers, "", "")
            }

            if (projects.length > 0) {
                projectsPart = getProjectsPart(projects)
            }
            return customersPart + (customersPart.length > 0 && projectsPart.length > 0 ? AND_DELIMITER : "") + projectsPart
        }
    }

    getUserSelectionDetailsString(userSelection) {
        if (userSelection.includeUserTeamOnly) {
            const usersFromSelectionUsersIds = this.getFilteredUsers(userSelection.selectedUsers)
            let selectedUsersAndUsersFromGroups = this._convertSelectedGroupsToSelectedUsersIfNecessary(usersFromSelectionUsersIds, userSelection)
                                                      .filter(user => this.manageableUserIdsSet[user.id])
            const res = []

            res.push(
                this.getTitle(
                    messageResource.getMessage("saved_reports.user_selector.title_header"),
                    selectedUsersAndUsersFromGroups,
                    this.userFormatter,
                )
            )
            return this.joinStrings(res, "\n", "\n")
        }

        return super.getUserSelectionDetailsString(userSelection)
    }

    getEvaDateParamString(dateSelection) {
        if (dateSelection.qualifier !== DateRangeSelectionQualifier.CUSTOM) {
            return messageResource.getMessage(EvaDateStateUtil[dateSelection.qualifier])
        }
        const date = dateSelection.date.parseJSONDate()
        return new DateFormat(this.dateFormat).formatDate(date) + ", " + Date.dayNames[date.getDay()]
    };

    getEvaDateDetailsString(dateSelection) {
        if (dateSelection.qualifier !== DateRangeSelectionQualifier.CUSTOM) {
            const date = dateSelection.date.parseJSONDate()
            return new DateFormat(this.dateFormat).formatDate(date) + ", " + Date.dayNames[date.getDay()]
        }
        return ""
    }

    getEvaDateCSVString(dateSelection) {
        const date = dateSelection.date
        return new DateFormat(this.dateFormat).formatDate(date) + ", " + Date.dayNames[date.getDay()]
    }

    getEvATaskDetailsString(taskParams, dateSelection) {
        let mainInfo = "", additionalInfo = ""
        switch (taskParams.tasksConfig) {
            case "ALL":
                mainInfo = messageResource.getMessage("report.budgeted_vs_actual.filter_view.open_and_completed_tasks")
                break
            case "OPEN_ONLY":
                mainInfo = messageResource.getMessage("report.budgeted_vs_actual.filter_view.open_tasks")
                break
            case "COMPLETED_ONLY":
                mainInfo = messageResource.getMessage("report.budgeted_vs_actual.filter_view.completed_tasks")
        }

        if (!taskParams.includeNotEstimated && !taskParams.includeNotStarted)
            additionalInfo = messageResource.getMessage("report.budgeted_vs_actual.except_started_after_and_not_estimated",
                [this.getEvaDateCSVString(dateSelection)])
        else if (!taskParams.includeNotStarted)
            additionalInfo = messageResource.getMessage("report.budgeted_vs_actual.except_started_after", [this.getEvaDateCSVString(dateSelection)])
        else if (!taskParams.includeNotEstimated)
            additionalInfo = messageResource.getMessage("report.budgeted_vs_actual.except_not_estimated")

        return mainInfo + " " + additionalInfo
    }

    _getAllUsersMessage() {
        const allUsersAvailable = this.reportAuthorizationInfo.allUsersAvailable
        const anyOtherUsersAvailable = _.isUndefined(this.reportAuthorizationInfo.anyOtherUsersAvailable) || this.reportAuthorizationInfo.anyOtherUsersAvailable

        if (!allUsersAvailable && anyOtherUsersAvailable) {
            return this.isViewerNotAssignedToTeam ?
                   messageResource.getMessage("users_selector.reports.team_users_and_me") :
                   messageResource.getMessage("common.report.all_staff.assigned_to_me")
        } else {
            return messageResource.getMessage("users_selector.reports.all_users")
        }
    }

    _getIncludeUserTeamOnlyMessage(users) {
        let filteredUsers = users.filter(user => this.manageableUserIdsSet[user.id])
        return filteredUsers.length === 1 ? this.userFormatter.format(filteredUsers[0]) : `${filteredUsers.length} ${userNameLower(filteredUsers.length)}`
    }

    _convertSelectedGroupsToSelectedUsersIfNecessary(users, userSelection) {
        const convertedUsers = UserSelectorUtils.convertSelectedGroupsIntoSelectedUsersConsideringTeamOnlyIfNecessary(
            this.users,
            userSelection.groupType,
            userSelection.allUsers,
            userSelection.showDisabled,
            userSelection.selectedGroups,
            this.userGroupsFeatureEnabled,
            this.tzGroupsFeatureEnabled,
            userSelection.includeUserTeamOnly,
        )

        return $.merge(users, convertedUsers)
    }
}


// WEBPACK FOOTER //
// ./web/js/reports/reportParamsStringBuilder/ATReportParamsStringBuilder.js